import { useEffect, useState } from 'react'
import { Network } from 'lucide-react'
import { apiDelete, apiGet, apiPost } from '../lib/api'
import { useTenantId } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  THead,
  Th,
  Td,
  Tr,
} from '../components/ui'

interface ConnectorPool {
  id: string
  name: string
  description: string
  selection_policy: 'round_robin' | 'least_conn' | 'sticky'
  created_at: string
}

const policyLabels: Record<string, string> = {
  round_robin: 'Round Robin',
  least_conn: 'Least Connections',
  sticky: 'Sticky (Session)',
}

export default function AdminPoolsPage() {
  const tenantId = useTenantId()
  const { toast } = useToast()
  const [pools, setPools] = useState<ConnectorPool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    name: '',
    description: '',
    selection_policy: 'round_robin' as const,
  })
  const [saving, setSaving] = useState(false)

  const fetchPools = async () => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    try {
      const data = await apiGet<ConnectorPool[]>(`/api/v1/tenants/${tenantId}/connector-pools`)
      setPools(data ?? [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchPools() }, [tenantId])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/connector-pools`, form)
      setForm({ name: '', description: '', selection_policy: 'round_robin' })
      setShowForm(false)
      toast({ title: 'Pool created', variant: 'success' })
      fetchPools()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Create failed', description: e.message, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this pool? Connectors will be unassigned.')) return
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/connector-pools/${id}`)
      toast({ title: 'Pool deleted', variant: 'success' })
      fetchPools()
    } catch (e: any) {
      setError(e.message)
      toast({ title: 'Delete failed', description: e.message, variant: 'danger' })
    }
  }

  return (
    <div>
      <PageHeader
        title="Connector Pools"
        description="Organize connectors into pools for routing and failover."
        actions={
          <Button onClick={() => setShowForm(true)}>+ New Pool</Button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : pools.length === 0 ? (
        <EmptyState
          icon={<Network className="h-8 w-8" />}
          title="No connector pools yet"
          description="Create one to get started."
          actionLabel="New Pool"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <DataTable>
          <THead>
            <Tr>
              <Th>Name</Th>
              <Th>ID</Th>
              <Th>Policy</Th>
              <Th className="text-right">Actions</Th>
            </Tr>
          </THead>
          <tbody>
            {pools.map((pool) => (
              <Tr key={pool.id}>
                <Td>
                  <div>
                    <p className="font-medium">{pool.name}</p>
                    {pool.description && <p className="text-xs text-muted">{pool.description}</p>}
                  </div>
                </Td>
                <Td className="font-mono text-xs text-muted">{pool.id}</Td>
                <Td>
                  <Badge variant="primary">{policyLabels[pool.selection_policy]}</Badge>
                </Td>
                <Td className="text-right">
                  <Button variant="ghost" size="sm" className="text-danger" onClick={() => handleDelete(pool.id)}>
                    Delete
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Create Connector Pool"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" form="pool-form" disabled={saving}>
              {saving ? 'Creating…' : 'Create Pool'}
            </Button>
          </>
        }
      >
        <form id="pool-form" onSubmit={handleCreate} className="space-y-4">
          <FormField label="Name *" htmlFor="pool-name">
            <Input
              id="pool-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Chennai DC"
            />
          </FormField>
          <FormField label="Description" htmlFor="pool-desc">
            <Input
              id="pool-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional"
            />
          </FormField>
          <FormField label="Selection Policy" htmlFor="pool-policy">
            <Select
              id="pool-policy"
              value={form.selection_policy}
              onChange={(e) => setForm({ ...form, selection_policy: e.target.value as any })}
            >
              <option value="round_robin">Round Robin</option>
              <option value="least_conn">Least Connections</option>
              <option value="sticky">Sticky (Session)</option>
            </Select>
          </FormField>
        </form>
      </Modal>
    </div>
  )
}
